import React from "react";
import KpiTile from "../components/KpiTile";
import InfoTooltip from "../components/InfoTooltip";
import { clickableProps } from "../components/clickable";
import {
  SHORT_PERIOD_LABELS,
  NET_CHANGE_PERIOD_PHRASES,
} from "../config/periods";

/*
Block 5 - the second KPI row, from GET dashboard/case-snapshot/.

Shares its resource with NeedsAttentionSection: one request feeds both blocks.
Only `cases.open.net_change` and `cases.completed` move with the period filter -
the other counts describe the live, current state and stay put regardless of
`period` (see the note in TicketNew.js).

Each card is a click-to-filter control for the case list, using the matching
`filters` entry from the same response. DOA claims overlaps the others (a DOA
case can also be pending approval) - that's expected.
*/
const CaseSnapshotSection = function (props) {
  const { resource, period, activeFilterId, onSelectFilter } = props;
  const data = resource.data;
  const cases = (data && data.cases) || null;
  const filters = (data && data.filters) || null;
  const shortPeriodLabel = SHORT_PERIOD_LABELS[period];
  const netChangePeriodPhrase = NET_CHANGE_PERIOD_PHRASES[period];
  const loading = resource.loading && !resource.hasLoadedOnce;

  const netChange = cases && cases.open ? cases.open.net_change : null;
  const formatDelta = function (value) {
    if (value === null || value === undefined) return null;
    const suffix = netChangePeriodPhrase ? " " + netChangePeriodPhrase : "";
    return (value > 0 ? "+" : "") + value + suffix;
  };
  const deltaTone = function (value) {
    if (value === null || value === undefined) return "neutral";
    if (value > 0) return "up";
    if (value < 0) return "down";
    return "neutral";
  };

  // The whole card is the hit area, not just the tile inside it.
  const cardProps = function (key) {
    const base = "tn-card tn-card-tight";
    if (!onSelectFilter || !filters || !filters[key]) return { className: base };
    const id = "snapshot:" + key;
    const active = activeFilterId === id;
    return {
      className:
        base + " tn-clickable" + (active ? " tn-clickable-active" : ""),
      ...clickableProps(function () {
        onSelectFilter(id);
      }, active),
    };
  };

  if (resource.error) {
    return (
      <div className="tn-alert tn-alert-block" role="alert">
        <span className="tn-alert-text">{resource.error}</span>
        <button
          type="button"
          className="tn-alert-retry"
          onClick={resource.reload}
        >
          Retry
        </button>
      </div>
    );
  }

  const completedLabel = shortPeriodLabel
    ? "Completed (" + shortPeriodLabel + ")"
    : "Completed";

  return (
    <div className="tn-snapshot-row">
      <div {...cardProps("open")}>
        <InfoTooltip text="Cases currently open and not yet completed. The delta below shows the net change over the selected period." />
        <KpiTile
          label="Open cases"
          value={cases && cases.open ? cases.open.count : null}
          loading={loading}
          delta={formatDelta(netChange)}
          deltaTone={deltaTone(netChange)}
        />
      </div>
      <div {...cardProps("pending_approval")}>
        <InfoTooltip text="Cases waiting on admin approval before they can move forward." />
        <KpiTile
          label="Pending approval"
          value={
            cases && cases.pending_approval
              ? cases.pending_approval.count
              : null
          }
          loading={loading}
          delta="Action needed"
          deltaTone="warn"
        />
      </div>
      <div {...cardProps("completed")}>
        <InfoTooltip text="Cases marked completed within the selected period." />
        <KpiTile
          label={completedLabel}
          value={cases && cases.completed ? cases.completed.count : null}
          loading={loading}
          delta="On track"
          deltaTone="up"
        />
      </div>
      <div {...cardProps("doa_claims")}>
        <InfoTooltip text="Cases flagged as dead-on-arrival (DOA) claims." />
        <KpiTile
          label="DOA claims"
          value={cases && cases.doa_claims ? cases.doa_claims.count : null}
          loading={loading}
          delta="Priority"
          deltaTone="neutral"
        />
      </div>
    </div>
  );
};

export default CaseSnapshotSection;
